import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import gridClasses from '../../../styles/grid.module.css'
import classes from './ViewCourseLesson.module.css'
import { AuthenticatedHeader } from '../../../components/Header/AuthenticatedHeader'
import { Link, useRouteMatch, useLocation, useHistory } from 'react-router-dom'
import { ReactComponent as BackIcon } from '../../../assets/icons/back.svg'
import { ReactComponent as PreviousIcon } from '../../../assets/icons/arrow-back.svg'
import { ReactComponent as NextIcon } from '../../../assets/icons/arrow-forward.svg'
import DownloadIcon from '../../../assets/icons/visibility.svg'
import { ReactComponent as CheckIcon } from '../../../assets/icons/check.svg'
import CourseOutlineProgressCard from '../../../components/Cards/CourseOutlineProgressCard/CourseOutlineProgressCard'
import { VideoPlayer } from '../../../components/VideoPlayer/VideoPlayer'
import Footer from '../../../components/Footer/Footer'
import { useDispatch, useSelector } from 'react-redux'
import { courseDetailsSelectors } from '../CourseDetails/courseDetailsSlice'
import { viewCourseLessonSelectors } from './viewCourseLessonSlice'
import { getCourse } from '../CourseDetails/courseDetailsApi'
import { getTopicContents } from './viewCourseLessonApi'
import notification from '../../../components/Notification/notification'
import Loading from '../../../components/loaders/Loading'
import { getImageURL, getParentRoute } from '../../../util/functions'
import {
    getCourseProgress,
    markTopicAsDone,
    setTopicAsCurrent,
} from './courseProgressApi'
import { courseProgressSelectors } from './courseProgressSlice'
import Modal from '../../../components/Modal/Modal'
import { Button } from '../../../components/Button/Button'
import successCurveTop from '../../../assets/images/success-cart-curve-top.svg'
import successCurveBottom from '../../../assets/images/success-cart-curve-bottom.svg'
import successIcon from '../../../assets/images/cart-success-congratulations-icon.svg'
import { userSelectors } from '../../../redux/userSlice'
import { UserTypes } from '../../../util/constants'
import { courseLessonSelectors } from './courseLessonSlice'
import { markLessonAsCompletedApi } from './courseLessonApi'

const CourseProgressIndicator = ({ totalLectures, completedLectures }) => (
    <div className={classes.courseProgress}>
        <div className={classes.courseProgressCircle}></div>
        <p className={classes.courseProgressText}>
            {completedLectures} of {totalLectures} Lectures Completed
        </p>
    </div>
)

const LectureAttachment = ({ filename, downloadLink }) => (
    <div className={classes.lectureAttachment}>
        <p className={classes.lectureAttachmentFilename}>{filename}</p>
        <a
            className={classes.lectureAttachmentButton}
            href={downloadLink}
            download={filename}
            target="_blank"
            rel="noopener noreferrer"
        >
            Download{' '}
            <img src={DownloadIcon} width={20} style={{ marginLeft: 10 }} />
        </a>
    </div>
)

const NavLinks = ({
    nextLectureId,
    previousLectureId,
    classId,
    topicId,
    isBeneficiary,
}) => {
    const { pathname } = useLocation()
    const { push } = useHistory()
    const dispatch = useDispatch()
    const [showCongratulationsModal, setShowCongratulationsModal] = useState(
        false
    )

    const hasPreviousLecture = !!previousLectureId
    const hasNextLecture = !!nextLectureId

    const lectureRoute = getParentRoute(pathname)

    const goToNextLecture = async event => {
        event.preventDefault()
        event.stopPropagation()
        await dispatch(markTopicAsDone({ classId, topicId })).catch(() => {})
        if (hasNextLecture) push(`${lectureRoute}/${nextLectureId}`)
        else setShowCongratulationsModal(true)
    }

    return (
        <React.Fragment>
            <Link
                className={classNames(
                    classes.navLink,
                    classes.prevLink,
                    !hasPreviousLecture && classes.navLinkDisabled
                )}
                tabIndex={!hasPreviousLecture ? -1 : undefined}
                to={
                    hasPreviousLecture
                        ? `${lectureRoute}/${previousLectureId}`
                        : ''
                }
            >
                <span className={classes.navLinkIcon}>
                    <PreviousIcon />
                </span>
                <span className={classes.navLinkText}>Previous</span>
            </Link>
            <Link
                className={classNames(classes.navLink, classes.nextLink)}
                tabIndex={!hasNextLecture ? -1 : undefined}
                onClick={goToNextLecture}
                to={undefined}
            >
                <span className={classes.navLinkText}>
                    {hasNextLecture ? 'Next' : 'Done'}
                </span>
                <span className={classes.navLinkIcon}>
                    {hasNextLecture ? <NextIcon /> : <CheckIcon />}
                </span>
            </Link>
            <Modal
                open={showCongratulationsModal}
                className={classes.successModal}
                allowDismiss={false}
            >
                <img
                    src={successCurveTop}
                    alt="Curve"
                    className={classes.successModalCurveTop}
                />
                <img
                    src={successCurveBottom}
                    alt="Curve"
                    className={classes.successModalCurveBottom}
                />
                <img
                    src={successIcon}
                    alt="Curve"
                    className={classes.successModalIcon}
                />
                <h1 className={classes.successModalHeader}>Congratulations</h1>
                <p className={classes.successModalText}>
                    You have completed the course!
                </p>
                <Button
                    to={
                        isBeneficiary
                            ? '/app/beneficiary/my-courses'
                            : '/app/my-courses'
                    }
                    bold
                >
                    My courses
                </Button>
            </Modal>
        </React.Fragment>
    )
}

// See CourseLectureContent.js on admin code for allowed file types
const isImageFile = format => ['jpg', 'png'].includes(format)

function _renderTopicContent(content) {
    switch (content.contenttype.toLowerCase()) {
        case 'text':
            return (
                <p
                    className={classes.lectureText}
                    dangerouslySetInnerHTML={{ __html: content.content }}
                />
            )
        case 'file':
            if (isImageFile(content.fileformat))
                return (
                    <img
                        className={classes.lectureImage}
                        src={getImageURL(content.attachment)}
                        alt=""
                    />
                )
            else
                return (
                    <LectureAttachment
                        filename={content.content}
                        downloadLink={getImageURL(content.attachment)}
                    />
                )
        case 'video url':
            return (
                <VideoPlayer
                    className={classes.video}
                    sources={[{ src: content.content }]}
                />
            )
        default:
            if (process.env.NODE_ENV === 'development')
                console.warn(
                    `Encountered content with unknown type ${content.contenttype}`,
                    content
                )
            return null
    }
}

export const LessonContent = ({ courseId, topicId, classId }) => {
    const dispatch = useDispatch()

    const isFetchingCourseDetails = useSelector(
        courseDetailsSelectors.isFetching
    )
    const isFetchingTopicContents = useSelector(
        viewCourseLessonSelectors.isFetching
    )
    const isFetchingCourseProgress = useSelector(
        courseProgressSelectors.isFetching
    )

    const failedToFetchCourseDetails = useSelector(
        courseDetailsSelectors.hasError
    )
    const failedToFetchTopicContents = useSelector(
        viewCourseLessonSelectors.hasError
    )
    const failedToFetchCourseProgress = useSelector(
        courseProgressSelectors.hasError
    )

    const courseDetailsErrorMessage = useSelector(
        courseDetailsSelectors.errorMessage
    )
    const topicContentsErrorMessage = useSelector(
        viewCourseLessonSelectors.errorMessage
    )
    const courseProgressErrorMessage = useSelector(
        courseProgressSelectors.errorMessage
    )

    const isFetchingMarkLessonAsCompleted = useSelector(
        courseLessonSelectors.isFetching
    ).markLessonAsCompleted
    const markLessonAsCompletedErrorMessage = useSelector(
        courseLessonSelectors.errorMessage
    ).markLessonAsCompleted
    const markLessonAsCompletedHasError = useSelector(
        courseLessonSelectors.hasError
    ).markLessonAsCompleted
    const markLessonAsCompletedIsSuccess = useSelector(
        courseLessonSelectors.isSuccess
    ).markLessonAsCompleted

    useEffect(() => {
        if (markLessonAsCompletedHasError) {
            notification.failure({
                title: 'Failed to load reach data',
                description: markLessonAsCompletedErrorMessage,
            })
        } else if (markLessonAsCompletedIsSuccess) {
            notification.success({
                title: 'Lesson marked as completed',
            })
        }
    }, [markLessonAsCompletedHasError, markLessonAsCompletedIsSuccess])

    const userType = useSelector(userSelectors.userType)
    const isBeneficiary = userType === UserTypes.Beneficiary

    const courseDetails = useSelector(courseDetailsSelectors.courseDetails)
    const { lessons: emptyState, courseLessons } = courseDetails

    const topicContents = useSelector(viewCourseLessonSelectors.topicContents)

    const courseProgress = useSelector(courseProgressSelectors.courseProgress)

    const isFetching =
        isFetchingCourseDetails ||
        isFetchingTopicContents ||
        isFetchingCourseProgress
    const hasError =
        failedToFetchCourseDetails ||
        failedToFetchTopicContents ||
        failedToFetchCourseProgress

    const courseCode = courseDetails?.coursecode

    // Workaround: the empty state of the courseDetails slice
    // defines the lessons under `lessons`, but the API returns `courseLessons`
    const lessons =
        emptyState ??
        courseLessons.map(lesson => ({
            ...lesson,
            topics: lesson?.lessonTopics?.map(topic => ({
                ...topic,
                ...((courseProgress.find(t => t.topicId === topic.topicid) ??
                    {}) ||
                    []),
            })),
        }))

    const lectures =
        lessons
            ?.map(lesson => lesson.topics)
            .reduce((acc, topics) => acc.concat(topics), []) ?? []
    const totalLectures = lectures.length
    const completedLectures = lectures?.filter(topic =>
        Boolean(topic?.completed)
    ).length

    useEffect(() => {
        if (courseCode !== courseId) {
            dispatch(getCourse(courseId))
        }
    }, [dispatch, courseCode, courseId])

    useEffect(() => {
        dispatch(getTopicContents(topicId)).then(() => {
            return dispatch(getCourseProgress(classId)).then(() => {
                dispatch(setTopicAsCurrent({ classId, topicId }))
            })
        })
    }, [dispatch, topicId, classId])

    useEffect(() => {
        if (failedToFetchCourseDetails) {
            notification.failure({
                title: 'Failed to get course',
                message: courseDetailsErrorMessage,
            })
        }
    }, [failedToFetchCourseDetails, courseDetailsErrorMessage])

    useEffect(() => {
        if (failedToFetchTopicContents) {
            notification.failure({
                title: 'Failed to get lesson',
                message: topicContentsErrorMessage,
            })
        }
    }, [failedToFetchTopicContents, topicContentsErrorMessage])

    useEffect(() => {
        if (failedToFetchCourseProgress)
            notification.failure({
                title: 'Failed to get lesson',
                message: courseProgressErrorMessage,
            })
    }, [failedToFetchCourseProgress, courseProgressErrorMessage])

    const currentLectureIndex = lectures.findIndex(
        lecture => `${lecture?.topicid}` === `${topicId}`
    )
    const hasPreviousLecture = currentLectureIndex > 0
    const hasNextLecture = currentLectureIndex < lectures.length - 1

    const previousLectureId = hasPreviousLecture
        ? lectures[currentLectureIndex - 1].topicid
        : ''
    const nextLectureId = hasNextLecture
        ? lectures[currentLectureIndex + 1]?.topicid
        : ''

    const markLessonAsCompleted = () => {
        //@todo: Hide the mark as completed button when its success
        dispatch(markLessonAsCompletedApi())
    }

    return (
        <React.Fragment>
            {!isFetchingCourseDetails && (
                <div
                    className={classNames(
                        gridClasses.container,
                        classes.topBarContainer
                    )}
                >
                    <div className={classes.topBarLeft}>
                        <div className={classes.lectureMeta}>
                            <Link
                                to={
                                    isBeneficiary
                                        ? '/app/beneficiary/my-courses'
                                        : '/app/my-courses'
                                }
                                className={classes.backLink}
                            >
                                <BackIcon />
                                Back to my courses
                            </Link>
                            <h3 className={classes.lectureTitle}>
                                {courseDetails?.title}
                            </h3>
                        </div>
                        <CourseProgressIndicator
                            totalLectures={totalLectures}
                            completedLectures={completedLectures}
                        />
                    </div>
                    <div className={classes.topBarRight}>
                        <NavLinks
                            previousLectureId={previousLectureId}
                            nextLectureId={nextLectureId}
                            classId={classId}
                            topicId={topicId}
                            isBeneficiary={isBeneficiary}
                        />
                    </div>
                </div>
            )}
            <main
                className={classNames(
                    gridClasses.container,
                    classes.pageContainer
                )}
            >
                <section className={classes.contentSection}>
                    {isFetching && <Loading compact />}
                    {!isFetching &&
                        !hasError &&
                        topicContents.map(_renderTopicContent)}
                    {!isFetching && !hasError && (
                        <div className={classes.bottomNavLinks}>
                            <NavLinks
                                previousLectureId={previousLectureId}
                                nextLectureId={nextLectureId}
                                topicId={topicId}
                                classId={classId}
                                isBeneficiary={isBeneficiary}
                            />
                        </div>
                    )}
                </section>
                <aside className={classes.outlineSection}>
                    {!isFetchingCourseDetails && !hasError && (
                        <CourseOutlineProgressCard
                            topics={lectures}
                            sections={lessons}
                            currentTopic={topicId}
                        />
                    )}
                </aside>
            </main>
        </React.Fragment>
    )
}

const ViewCourseLesson = () => {
    const { params } = useRouteMatch()
    const { courseId, topicId, enrollmentId: classId } = params

    return (
        <React.Fragment>
            <AuthenticatedHeader />
            <LessonContent
                topicId={topicId}
                classId={classId}
                courseId={courseId}
            />
            <Footer />
        </React.Fragment>
    )
}

export default ViewCourseLesson
